import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import CO2Visualization from '../analytics/CO2Visualization';
import { analyticsData } from '../../services/analyticsData';
import { analyticsService } from '../../services/analyticsService';
import { X, Loader2 } from 'lucide-react';
import { useSidebar } from '../../contexts/SidebarContext';
import { getSchichtType } from '../../utils/schichtType';
import { VERGLEICH_PROJECTS } from '../../config/vergleichProjects';



const ProjectSearchModal = ({ isOpen, onClose, onProjectSelect }) => {
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');

  // Load projects when modal opens
  useEffect(() => {
    if (isOpen && projects.length === 0) {
      loadProjects();
    }
  }, [isOpen, projects.length]);

  // Filter projects based on search input
  useEffect(() => {
    if (searchInput) {
      const filtered = projects.filter(project =>
        project.displayName.toLowerCase().includes(searchInput.toLowerCase())
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects(projects);
    }
  }, [searchInput, projects]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    setError('');
    try {
      setProjects(VERGLEICH_PROJECTS);
      setFilteredProjects(VERGLEICH_PROJECTS);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Error loading projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  if (!isOpen) return null;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput) {
      const matchedProject = filteredProjects.find(project =>
        project.displayName === searchInput
      );
      if (matchedProject) {
        handleProjectSelect(matchedProject);
      }
    }
  };

  const handleProjectSelect = (project) => {
    setIsLoading(true);
    // Simulate loading for a brief moment
    setTimeout(() => {
      setIsLoading(false);
      onProjectSelect(project);
      onClose();
      setSearchInput('');
    }, 500);
  };

  const handleProjectClick = (project) => {
    setSearchInput(project.displayName);
    handleProjectSelect(project);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-xl mb-4">Projekt laden</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSearch} className="space-y-4">
          <div className="relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full p-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Projekt suchen"
              autoFocus
              disabled={loadingProjects}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
              {loadingProjects ? 'Laden...' : `${filteredProjects.length} Ergebnisse`}
            </span>
          </div>

          {loadingProjects && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-green-500" />
            </div>
          )}

          {!loadingProjects && (
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
              {filteredProjects.map((project) => (
                <button
                  key={project.fullName}
                  type="button"
                  onClick={() => handleProjectClick(project)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors flex items-center"
                >
                  <div>
                    <span className="font-medium">{project.displayName}</span>
                  </div>
                </button>
              ))}
              {filteredProjects.length === 0 && (
                <div className="px-4 py-2 text-gray-500">
                  Keine Projekte gefunden
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading || !searchInput.trim()}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors disabled:bg-green-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Laden...</span>
                </>
              ) : (
                <span>Suchen</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AnalyticsHeader = ({ selectedProject, onProjectSelect, timeframe }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleProjectSelect = (projectName) => {
    onProjectSelect(projectName);
  };

  // Helper function to format date range
  const formatTimeframe = (timeframe) => {
    if (!timeframe || !timeframe.start || !timeframe.end) {
      return "Projektzeitraum: Nicht verfügbar";
    }

    try {
      const startDate = new Date(timeframe.start);
      const endDate = new Date(timeframe.end);

      // Format as DD.MM.YY
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
        return `${day}.${month}.${year}`;
      };

      return `Projektzeitraum: ${formatDate(startDate)}-${formatDate(endDate)}`;
    } catch (error) {
      console.error('Error formatting timeframe:', error);
      return "Projektzeitraum: Nicht verfügbar";
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl">
            Vergleich Projekt{" "}
            <button
              onClick={() => setIsModalOpen(true)}
              className="font-semibold text-green-500 hover:text-green-600 transition-colors focus:outline-none"
            >
              {selectedProject.displayName}
            </button>
          </h1>
          {/* <p className="text-gray-500">{formatTimeframe(timeframe)}</p> */}
        </div>
        <div className="flex items-center space-x-4">
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">

          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">

          </button>
          <div className="flex items-center space-x-2">
            <img
              src="/logos/UserLogo.png"
              alt="Profile"
              className="h-8 w-8 rounded-full"
            />
            <span className="text-gray-700 font-medium">Autobahn GmbH</span>
          </div>
        </div>
      </div>

      <ProjectSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProjectSelect={handleProjectSelect}
      />
    </>
  );
};




const KeyMetrics = ({ metrics, co2Data, comparisonCo2Data, selectedProject, layerAreas }) => {
  // Helper function to format numbers with German formatting
  const formatGermanNumber = (value) => {
    // Extract number from string like "1,247.5 t" or "462 t CO2e"
    const match = value.match(/[\d,]+\.?\d*/);
    if (!match) return value;

    const numberStr = match[0];
    // Convert to actual number (handle both comma and period as decimal separator)
    const number = parseFloat(numberStr.replace(/,/g, ''));

    if (isNaN(number)) return value;

    // Format with German locale and add unit back
    const unit = value.replace(numberStr, '').trim();
    const formattedNumber = number.toLocaleString('de-DE');

    // Handle CO₂ formatting
    const formattedUnit = unit.replace(/CO2/g, 'CO₂');

    return `${formattedNumber} ${formattedUnit}`.trim();
  };

  // Helper function to render layer areas breakdown
  const renderLayerAreasBreakdown = () => {
    if (!layerAreas) {
      // Show placeholder when no layer areas data is available
      return (
        <div className="text-xs mt-2 text-gray-400 italic">
          Keine Flächenangaben vorhanden
        </div>
      );
    }

    const formatArea = (value) => {
      if (value === undefined || value === null || value === 0) return '0';
      return value.toLocaleString('de-DE');
    };

    return (
      <div className="text-sm mt-2 text-black font-medium">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">DS</div>
          <div className="text-center">BS</div>
          <div className="text-center">TS</div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center whitespace-nowrap">{formatArea((layerAreas.deckschichten || []).reduce((s, e) => s + (e.flaeche || 0), 0))} m²</div>
          <div className="text-center whitespace-nowrap">{formatArea((layerAreas.binderschichten || []).reduce((s, e) => s + (e.flaeche || 0), 0))} m²</div>
          <div className="text-center whitespace-nowrap">{formatArea((layerAreas.tragschichten || []).reduce((m, e) => Math.max(m, e.flaeche || 0), 0))} m²</div>
        </div>
      </div>
    );
  };



  // Calculate simple trend for Emissionsfaktor (without 1990 comparison)
  const calculateEmissionsfaktorTrend = () => {
    // For comparison page, show neutral trend
    return "0,0%";
  };



  // Calculate emission factors for all four project types
  const calculateEmissionFactors = () => {
    const combinedData = combineProjectsForProjectComparison(co2Data, comparisonCo2Data, selectedProject.fullName);

    if (!combinedData || !combinedData.materials) {
      return { ef1990: null, efKonventionell: null, efCO2Massnahme: null, efIst: null };
    }

    const ef1990 = combinedData.materials.find(m => m.name === '1990');
    const efIst = combinedData.materials.find(m => m.name === 'CO2 IST');
    const efKonventionell = combinedData.materials.find(m => m.name === 'Konventionell');
    const efCO2Massnahme = combinedData.materials.find(m => m.name === 'CO2 SOLL');

    return {
      ef1990: ef1990?.total || null,
      efIst: efIst?.total || null,
      efKonventionell: efKonventionell?.total || null,
      efCO2Massnahme: efCO2Massnahme?.total || null
    };
  };

  // Calculate total CO2 emissions for all four project types
  const calculateCO2Emissions = () => {
    const emissionFactors = calculateEmissionFactors();

    // Get total quantity from layer areas or use a default
    let totalQuantity = 0;
    if (layerAreas) {
      const sumMenge = (arr) => (arr || []).reduce((s, e) => s + (e.menge || 0), 0);
      totalQuantity = sumMenge(layerAreas.deckschichten) +
        sumMenge(layerAreas.binderschichten) +
        sumMenge(layerAreas.tragschichten);
    }

    // If no layer areas data, try to get quantity from combined data
    if (totalQuantity === 0) {
      const combinedData = combineProjectsForProjectComparison(co2Data, comparisonCo2Data, selectedProject.fullName);
      if (combinedData && combinedData.materials && combinedData.materials.length > 0) {
        totalQuantity = combinedData.materials[0]?.totalQuantity || 1000; // Default fallback
      }
    }

    return {
      co2_1990: emissionFactors.ef1990 ? (emissionFactors.ef1990 * totalQuantity / 1000) : null, // Convert kg to t
      co2_Ist: emissionFactors.efIst ? (emissionFactors.efIst * totalQuantity / 1000) : null,
      co2_Konventionell: emissionFactors.efKonventionell ? (emissionFactors.efKonventionell * totalQuantity / 1000) : null,
      co2_CO2Massnahme: emissionFactors.efCO2Massnahme ? (emissionFactors.efCO2Massnahme * totalQuantity / 1000) : null
    };
  };

  // Update metrics with modified labels/values and emission factors
  const updatedMetrics = metrics.map(metric => {
    if (metric.name === "Emissionsfaktor") {
      const emissionFactors = calculateEmissionFactors();
      return {
        ...metric,
        name: "Emissionsfaktor (kg CO₂e/t)", // Add unit to label
        value: metric.value.replace(/\s*(kg\s*CO2?e?\/t|kg\s*CO₂e\/t)/i, '').trim(), // Remove unit from value
        trend: calculateEmissionsfaktorTrend(), // Use simple trend calculation
        emissionFactors: emissionFactors // Add emission factors data
      };
    } else if (metric.name === "CO₂e Emissionen") {
      const co2Emissions = calculateCO2Emissions();
      return {
        ...metric,
        name: "CO₂e Emissionen (t CO₂e)", // Add unit to label
        value: metric.value.replace(/\s*(t\s*CO2?e?|t\s*CO₂e)/i, '').trim(), // Remove unit from value
        co2Emissions: co2Emissions // Add CO2 emissions data
      };
    }
    return metric;
  });

  // Swap positions: find indices and swap CO₂e Emissionen and Emissionsfaktor
  const co2Index = updatedMetrics.findIndex(m => m.name.startsWith("CO₂e Emissionen"));
  const emissionIndex = updatedMetrics.findIndex(m => m.name.startsWith("Emissionsfaktor"));

  if (co2Index !== -1 && emissionIndex !== -1) {
    [updatedMetrics[co2Index], updatedMetrics[emissionIndex]] = [updatedMetrics[emissionIndex], updatedMetrics[co2Index]];
  }

  const allMetrics = updatedMetrics;

  return (
    <div className="grid grid-cols-3 gap-4">
      {allMetrics.map((metric, index) => (
        <Card key={index}>
          <div className="p-4">
            <div className="text-sm font-semibold text-black">{metric.name}</div>
            {metric.name.startsWith("Emissionsfaktor") ? (
              // Show four emission factors side by side
              <div className="mt-3">
                <div className="grid grid-cols-4 gap-2">
                  {/* 1990 */}
                  <div className="text-center">
                    <div className="text-xs text-black mb-2">1990</div>
                    <div className="text-xl font-bold">
                      {metric.emissionFactors?.ef1990 ?
                        metric.emissionFactors.ef1990.toFixed(1).replace('.', ',') :
                        '—'
                      }
                    </div>
                  </div>

                  {/* Konventionell */}
                  <div className="text-center">
                    <div className="text-xs text-black mb-2">Konventionell</div>
                    <div className="text-xl font-bold">
                      {metric.emissionFactors?.efKonventionell ?
                        metric.emissionFactors.efKonventionell.toFixed(1).replace('.', ',') :
                        '—'
                      }
                    </div>
                  </div>

                  {/* CO2 Maßnahme */}
                  <div className="text-center">
                    <div className="text-xs text-black mb-2">CO2 SOLL</div>
                    <div className="text-xl font-bold">
                      {metric.emissionFactors?.efCO2Massnahme ?
                        metric.emissionFactors.efCO2Massnahme.toFixed(1).replace('.', ',') :
                        '—'
                      }
                    </div>
                  </div>

                  {/* IST - Moved to last */}
                  <div className="text-center">
                    <div className="text-xs text-black mb-2">CO2 IST</div>
                    <div className="text-xl font-bold">
                      {metric.emissionFactors?.efIst ?
                        metric.emissionFactors.efIst.toFixed(1).replace('.', ',') :
                        '—'
                      }
                    </div>
                  </div>
                </div>
              </div>
            ) : metric.name.startsWith("CO₂e Emissionen") ? (
              // Show four CO2 emissions side by side
              <div className="mt-3">
                <div className="grid grid-cols-4 gap-2">
                  {/* 1990 */}
                  <div className="text-center">
                    <div className="text-xs text-black mb-2">1990</div>
                    <div className="text-xl font-bold">
                      {metric.co2Emissions?.co2_1990 ?
                        formatGermanNumber(Math.round(metric.co2Emissions.co2_1990).toString()) :
                        '—'
                      }
                    </div>
                  </div>

                  {/* Konventionell */}
                  <div className="text-center">
                    <div className="text-xs text-black mb-2">Konventionell</div>
                    <div className="text-xl font-bold">
                      {metric.co2Emissions?.co2_Konventionell ?
                        formatGermanNumber(Math.round(metric.co2Emissions.co2_Konventionell).toString()) :
                        '—'
                      }
                    </div>
                  </div>

                  {/* CO2 Maßnahme */}
                  <div className="text-center">
                    <div className="text-xs text-black mb-2">CO2 SOLL</div>
                    <div className="text-xl font-bold">
                      {metric.co2Emissions?.co2_CO2Massnahme ?
                        formatGermanNumber(Math.round(metric.co2Emissions.co2_CO2Massnahme).toString()) :
                        '—'
                      }
                    </div>
                  </div>

                  {/* IST - Moved to last */}
                  <div className="text-center">
                    <div className="text-xs text-black mb-2">CO2 IST</div>
                    <div className="text-xl font-bold">
                      {metric.co2Emissions?.co2_Ist ?
                        formatGermanNumber(Math.round(metric.co2Emissions.co2_Ist).toString()) :
                        '—'
                      }
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Regular single value display for other metrics
              <>
                <div className="text-2xl font-semibold mt-2">
                  <span>{formatGermanNumber(metric.value)}</span>
                </div>
                {metric.name === "Menge Asphaltmischgut" ? (
                  // Show layer areas breakdown for Asphaltmischgut
                  renderLayerAreasBreakdown()
                ) : (
                  <div className={`text-sm mt-2 ${metric.trend.startsWith('+') ? 'text-red-500' : 'text-green-500'}`}>
                    {metric.trend} vs. Referenzwert
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};

// Function to group materials by construction layer type with proper weighted aggregation
const groupMaterialsByLayer = (co2Data) => {
  if (!co2Data || !co2Data.materials || co2Data.materials.length === 0) {
    return co2Data;
  }

  // Initialize layer groups to collect materials and calculate weighted averages
  const layerGroups = {
    'Deckschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } },
    'Binderschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } },
    'Tragschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } }
  };

  // First pass: Group materials by layer type and collect quantities
  co2Data.materials.forEach(material => {
    const layerType = getSchichtType(material);

    if (!layerType) {
      console.warn(`Could not classify material: ${material.name}`);
      return;
    }

    const quantity = material.quantity || material.totalQuantity || 1;

    layerGroups[layerType].materials.push({
      ...material,
      quantity: quantity
    });

    layerGroups[layerType].totalQuantity += quantity;
  });

  // Second pass: Calculate weighted average emission factors and total CO2 emissions for each layer
  Object.keys(layerGroups).forEach(layerType => {
    const group = layerGroups[layerType];

    if (group.materials.length === 0 || group.totalQuantity === 0) {
      return; // Skip empty groups
    }

    // Calculate weighted sum of emission factors
    let weightedA1 = 0, weightedA2 = 0, weightedA3 = 0, weightedA4 = 0, weightedTotal = 0;

    group.materials.forEach(material => {
      // Calculate weighted emission factors: (emission_factor * material_quantity)
      weightedA1 += (material.A1 || 0) * material.quantity;
      weightedA2 += (material.A2 || 0) * material.quantity;
      weightedA3 += (material.A3 || 0) * material.quantity;
      weightedA4 += (material.A4 || 0) * material.quantity;
      weightedTotal += (material.total || 0) * material.quantity;
    });

    // Calculate weighted average emission factors: (EmissionFactor1 × Quantity1 + EmissionFactor2 × Quantity2) / (Quantity1 + Quantity2)
    group.totalCO2.A1 = weightedA1 / group.totalQuantity;
    group.totalCO2.A2 = weightedA2 / group.totalQuantity;
    group.totalCO2.A3 = weightedA3 / group.totalQuantity;
    group.totalCO2.A4 = weightedA4 / group.totalQuantity;
    group.totalCO2.total = weightedTotal / group.totalQuantity;
  });

  // Convert layer groups to materials array format
  const groupedMaterials = Object.entries(layerGroups)
    .filter(([layerType, group]) => group.materials.length > 0) // Only include layers with materials
    .map(([layerType, group]) => ({
      name: layerType,
      fullName: layerType,
      // Total CO2 emissions for the layer (sum of weighted emissions)
      A1: group.totalCO2.A1,
      A2: group.totalCO2.A2,
      A3: group.totalCO2.A3,
      A4: group.totalCO2.A4,
      total: group.totalCO2.total,
      // Additional metadata
      location: '',
      materialCount: group.materials.length,
      totalQuantity: group.totalQuantity,
      // For debugging/transparency
      materials: group.materials.map(m => m.name)
    }));

  console.log('Layer grouping results:', groupedMaterials);

  // Return modified co2Data with grouped materials
  return {
    ...co2Data,
    materials: groupedMaterials
  };
};

// Function to group materials by construction layer type using 1990 data
const groupMaterialsByLayer1990 = (co2Data) => {
  if (!co2Data || !co2Data.materials || co2Data.materials.length === 0) {
    return co2Data;
  }

  // Initialize layer groups to collect materials and calculate weighted averages
  const layerGroups = {
    'Deckschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } },
    'Binderschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } },
    'Tragschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } }
  };

  // First pass: Group materials by layer type and collect quantities (only materials with 1990 data)
  co2Data.materials.forEach(material => {
    if (!material.has1990Data) return; // Skip materials without 1990 data

    const layerType = getSchichtType(material);

    // If we can't classify, skip this material
    if (!layerType) {
      console.warn(`Could not classify material: ${material.name}`);
      return;
    }

    // Get material quantity (assuming it's available in the material data)
    const quantity = material.quantity || material.totalQuantity || 1; // Default to 1 if no quantity available

    // Add material to the appropriate layer group with 1990 data
    layerGroups[layerType].materials.push({
      ...material,
      quantity: quantity,
      A1: material.A1_1990 || 0,
      A2: material.A2_1990 || 0,
      A3: material.A3_1990 || 0,
      A4: material.A4_1990 || 0,
      total: material.total_1990 || 0
    });

    // Add to total quantity for this layer
    layerGroups[layerType].totalQuantity += quantity;
  });

  // Second pass: Calculate weighted average emission factors for each layer
  Object.keys(layerGroups).forEach(layerType => {
    const group = layerGroups[layerType];

    if (group.materials.length === 0 || group.totalQuantity === 0) {
      return; // Skip empty groups
    }

    // Calculate weighted sum of emission factors
    let weightedA1 = 0, weightedA2 = 0, weightedA3 = 0, weightedA4 = 0, weightedTotal = 0;

    group.materials.forEach(material => {
      // Calculate weighted emission factors: (emission_factor * material_quantity)
      weightedA1 += (material.A1 || 0) * material.quantity;
      weightedA2 += (material.A2 || 0) * material.quantity;
      weightedA3 += (material.A3 || 0) * material.quantity;
      weightedA4 += (material.A4 || 0) * material.quantity;
      weightedTotal += (material.total || 0) * material.quantity;
    });

    // Calculate weighted average emission factors: (EmissionFactor1 × Quantity1 + EmissionFactor2 × Quantity2) / (Quantity1 + Quantity2)
    group.totalCO2.A1 = weightedA1 / group.totalQuantity;
    group.totalCO2.A2 = weightedA2 / group.totalQuantity;
    group.totalCO2.A3 = weightedA3 / group.totalQuantity;
    group.totalCO2.A4 = weightedA4 / group.totalQuantity;
    group.totalCO2.total = weightedTotal / group.totalQuantity;
  });

  // Convert layer groups to materials array format
  const groupedMaterials = Object.entries(layerGroups)
    .filter(([layerType, group]) => group.materials.length > 0) // Only include layers with materials
    .map(([layerType, group]) => ({
      name: layerType,
      fullName: layerType,
      // Weighted average emission factors for the layer
      A1: group.totalCO2.A1,
      A2: group.totalCO2.A2,
      A3: group.totalCO2.A3,
      A4: group.totalCO2.A4,
      total: group.totalCO2.total,
      // Additional metadata
      location: '',
      materialCount: group.materials.length,
      totalQuantity: group.totalQuantity,
      // For debugging/transparency
      materials: group.materials.map(m => m.name)
    }));

  console.log('1990 Layer grouping results:', groupedMaterials);

  // Return modified co2Data with grouped materials
  return {
    ...co2Data,
    materials: groupedMaterials
  };
};

// Function to group materials by construction layer type using IST data
const groupMaterialsByLayerIst = (co2Data) => {
  if (!co2Data || !co2Data.materials || co2Data.materials.length === 0) {
    return co2Data;
  }

  // Initialize layer groups to collect materials and calculate weighted averages
  const layerGroups = {
    'Deckschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } },
    'Binderschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } },
    'Tragschicht': { materials: [], totalQuantity: 0, totalCO2: { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 } }
  };

  // First pass: Group materials by layer type and collect quantities (only materials with IST data)
  co2Data.materials.forEach(material => {
    if (!material.hasIstData) return; // Skip materials without IST data

    const layerType = getSchichtType(material);

    // If we can't classify, skip this material
    if (!layerType) {
      console.warn(`Could not classify material: ${material.name}`);
      return;
    }

    // Get material quantity (assuming it's available in the material data)
    const quantity = material.quantity || material.totalQuantity || 1; // Default to 1 if no quantity available

    // Add material to the appropriate layer group with IST data
    layerGroups[layerType].materials.push({
      ...material,
      quantity: quantity,
      A1: material.A1_Ist || 0,
      A2: material.A2_Ist || 0,
      A3: material.A3_Ist || 0,
      A4: material.A4_Ist || 0,
      total: material.total_Ist || 0
    });

    // Add to total quantity for this layer
    layerGroups[layerType].totalQuantity += quantity;
  });

  // Second pass: Calculate weighted average emission factors for each layer
  Object.keys(layerGroups).forEach(layerType => {
    const group = layerGroups[layerType];

    if (group.materials.length === 0 || group.totalQuantity === 0) {
      return; // Skip empty groups
    }

    // Calculate weighted sum of emission factors
    let weightedA1 = 0, weightedA2 = 0, weightedA3 = 0, weightedA4 = 0, weightedTotal = 0;

    group.materials.forEach(material => {
      // Calculate weighted emission factors: (emission_factor * material_quantity)
      weightedA1 += (material.A1 || 0) * material.quantity;
      weightedA2 += (material.A2 || 0) * material.quantity;
      weightedA3 += (material.A3 || 0) * material.quantity;
      weightedA4 += (material.A4 || 0) * material.quantity;
      weightedTotal += (material.total || 0) * material.quantity;
    });

    // Calculate weighted average emission factors: (EmissionFactor1 × Quantity1 + EmissionFactor2 × Quantity2) / (Quantity1 + Quantity2)
    group.totalCO2.A1 = weightedA1 / group.totalQuantity;
    group.totalCO2.A2 = weightedA2 / group.totalQuantity;
    group.totalCO2.A3 = weightedA3 / group.totalQuantity;
    group.totalCO2.A4 = weightedA4 / group.totalQuantity;
    group.totalCO2.total = weightedTotal / group.totalQuantity;
  });

  // Convert layer groups to materials array format
  const groupedMaterials = Object.entries(layerGroups)
    .filter(([layerType, group]) => group.materials.length > 0) // Only include layers with materials
    .map(([layerType, group]) => ({
      name: layerType,
      fullName: layerType,
      // Weighted average emission factors for the layer
      A1: group.totalCO2.A1,
      A2: group.totalCO2.A2,
      A3: group.totalCO2.A3,
      A4: group.totalCO2.A4,
      total: group.totalCO2.total,
      // Additional metadata
      location: '',
      materialCount: group.materials.length,
      totalQuantity: group.totalQuantity,
      // For debugging/transparency
      materials: group.materials.map(m => m.name)
    }));

  console.log('IST Layer grouping results:', groupedMaterials);

  // Return modified co2Data with grouped materials
  return {
    ...co2Data,
    materials: groupedMaterials
  };
};

// Function to combine main project and comparison project data for layer-level comparison (9 bars)
const combineProjectsForLayerComparison = (mainData, comparisonData, selectedProjectName) => {
  if (!mainData) {
    return mainData; // Return main data if no main data available
  }

  // Determine which project is which based on the selected project name
  const isMainProjectCO2Massnahme = selectedProjectName && selectedProjectName.includes('CO2 Maßnahme');
  const isMainProjectKonventionell = selectedProjectName && selectedProjectName.includes('konventionell');

  // Group datasets by layer
  const mainGrouped = groupMaterialsByLayer(mainData);

  // Create combined materials array with main project data and optionally comparison data
  const combinedMaterials = [];

  // Get all layer types from main dataset and ensure proper order
  const layerOrder = ['Deckschicht', 'Binderschicht', 'Tragschicht'];
  const layerAbbreviations = {
    'Deckschicht': 'DS',
    'Binderschicht': 'BS',
    'Tragschicht': 'TS'
  };
  const mainLayers = mainGrouped.materials.map(m => m.name);

  // Sort layers according to the desired order
  const orderedLayers = layerOrder.filter(layer => mainLayers.includes(layer));

  // Prepare comparison data if available
  let comparisonGrouped = null;
  if (comparisonData) {
    comparisonGrouped = groupMaterialsByLayer(comparisonData);
  }

  // Prepare 1990 data if available (only from the main project)
  let main1990Grouped = null;
  if (mainData.materials && mainData.materials.some(m => m.has1990Data)) {
    main1990Grouped = groupMaterialsByLayer1990(mainData);
  }

  // Prepare IST data if available (only from the main project)
  let mainIstGrouped = null;
  if (mainData.materials && mainData.materials.some(m => m.hasIstData)) {
    mainIstGrouped = groupMaterialsByLayerIst(mainData);
  }

  // For each layer, add all variants in the correct order: 1990, Konventionell, CO2 SOLL, CO2 IST
  orderedLayers.forEach(layerName => {
    const mainLayer = mainGrouped.materials.find(m => m.name === layerName);
    const layerAbbrev = layerAbbreviations[layerName] || layerName;

    if (mainLayer) {
      // 1. Add 1990 data first (if available)
      if (main1990Grouped) {
        const main1990Layer = main1990Grouped.materials.find(m => m.name === layerName);
        if (main1990Layer) {
          combinedMaterials.push({
            ...main1990Layer,
            name: `${layerAbbrev} (1990)`,
            fullName: `${layerAbbrev} (1990)`,
            projectType: '1990'
          });
        }
      }

      // 2. Add konventionell data second
      if (isMainProjectKonventionell) {
        // Main project is konventionell
        combinedMaterials.push({
          ...mainLayer,
          name: `${layerAbbrev} (Konventionell)`,
          fullName: `${layerAbbrev} (Konventionell)`,
          projectType: 'konventionell'
        });
      } else if (comparisonGrouped) {
        // Comparison project is konventionell
        const comparisonLayer = comparisonGrouped.materials.find(m => m.name === layerName);
        if (comparisonLayer) {
          combinedMaterials.push({
            ...comparisonLayer,
            name: `${layerAbbrev} (Konventionell)`,
            fullName: `${layerAbbrev} (Konventionell)`,
            projectType: 'konventionell'
          });
        }
      }

      // 3. Add CO2 Maßnahme data third
      if (isMainProjectCO2Massnahme) {
        // Main project is CO2 Maßnahme
        combinedMaterials.push({
          ...mainLayer,
          name: `${layerAbbrev} (CO2 SOLL)`,
          fullName: `${layerAbbrev} (CO2 SOLL)`,
          projectType: 'co2_massnahme'
        });
      } else if (comparisonGrouped) {
        // Comparison project is CO2 Maßnahme
        const comparisonLayer = comparisonGrouped.materials.find(m => m.name === layerName);
        if (comparisonLayer) {
          combinedMaterials.push({
            ...comparisonLayer,
            name: `${layerAbbrev} (CO2 SOLL)`,
            fullName: `${layerAbbrev} (CO2 SOLL)`,
            projectType: 'co2_massnahme'
          });
        }
      }

      // 4. Add IST data fourth (if available)
      if (mainIstGrouped) {
        const mainIstLayer = mainIstGrouped.materials.find(m => m.name === layerName);
        if (mainIstLayer) {
          combinedMaterials.push({
            ...mainIstLayer,
            name: `${layerAbbrev} (CO2 IST)`,
            fullName: `${layerAbbrev} (CO2 IST)`,
            projectType: 'ist'
          });
        }
      }
    }
  });

  return {
    ...mainData,
    materials: combinedMaterials
  };
};

// Function to combine main project and comparison project data for project-level comparison (3 bars)
const combineProjectsForProjectComparison = (mainData, comparisonData, selectedProjectName) => {
  if (!mainData) {
    return mainData; // Return main data if no main data available
  }

  // Determine which project is which based on the selected project name
  const isMainProjectCO2Massnahme = selectedProjectName && selectedProjectName.includes('CO2 Maßnahme');
  const isMainProjectKonventionell = selectedProjectName && selectedProjectName.includes('konventionell');

  // Group datasets by layer first
  const mainGrouped = groupMaterialsByLayer(mainData);

  // Create project-level aggregated materials
  const projectMaterials = [];

  // Prepare comparison data if available
  let comparisonGrouped = null;
  if (comparisonData) {
    comparisonGrouped = groupMaterialsByLayer(comparisonData);
  }

  // Prepare 1990 data if available (only from the main project)
  let main1990Grouped = null;
  if (mainData.materials && mainData.materials.some(m => m.has1990Data)) {
    main1990Grouped = groupMaterialsByLayer1990(mainData);
  }

  // Prepare IST data if available (only from the main project)
  let mainIstGrouped = null;
  if (mainData.materials && mainData.materials.some(m => m.hasIstData)) {
    mainIstGrouped = groupMaterialsByLayerIst(mainData);
  }

  // Calculate project-level weighted averages
  const calculateProjectAverage = (groupedData, projectName) => {
    if (!groupedData || !groupedData.materials || groupedData.materials.length === 0) {
      return null;
    }

    let totalQuantity = 0;
    let weightedSumA1 = 0, weightedSumA2 = 0, weightedSumA3 = 0, weightedSumA4 = 0, weightedSumTotal = 0;

    groupedData.materials.forEach(layer => {
      const quantity = layer.totalQuantity || 1;
      totalQuantity += quantity;

      // Calculate weighted sums: EmissionFactor * Quantity
      weightedSumA1 += (layer.A1 || 0) * quantity;
      weightedSumA2 += (layer.A2 || 0) * quantity;
      weightedSumA3 += (layer.A3 || 0) * quantity;
      weightedSumA4 += (layer.A4 || 0) * quantity;
      weightedSumTotal += (layer.total || 0) * quantity;
    });

    // Calculate weighted averages: WeightedSum / TotalQuantity
    if (totalQuantity > 0) {
      return {
        name: projectName,
        fullName: projectName,
        A1: weightedSumA1 / totalQuantity,
        A2: weightedSumA2 / totalQuantity,
        A3: weightedSumA3 / totalQuantity,
        A4: weightedSumA4 / totalQuantity,
        total: weightedSumTotal / totalQuantity,
        location: '',
        totalQuantity: totalQuantity
      };
    }

    return null;
  };

  // 1. Add 1990 project average (if available)
  if (main1990Grouped) {
    const project1990 = calculateProjectAverage(main1990Grouped, '1990');
    if (project1990) {
      projectMaterials.push(project1990);
    }
  }

  // 2. Add Konventionell project average
  if (isMainProjectKonventionell) {
    // Main project is konventionell
    const projectKonventionell = calculateProjectAverage(mainGrouped, 'Konventionell');
    if (projectKonventionell) {
      projectMaterials.push(projectKonventionell);
    }
  } else if (comparisonGrouped) {
    // Comparison project is konventionell
    const projectKonventionell = calculateProjectAverage(comparisonGrouped, 'Konventionell');
    if (projectKonventionell) {
      projectMaterials.push(projectKonventionell);
    }
  }

  // 3. Add CO2 Maßnahme project average
  if (isMainProjectCO2Massnahme) {
    // Main project is CO2 Maßnahme
    const projectCO2Massnahme = calculateProjectAverage(mainGrouped, 'CO2 SOLL');
    if (projectCO2Massnahme) {
      projectMaterials.push(projectCO2Massnahme);
    }
  } else if (comparisonGrouped) {
    // Comparison project is CO2 Maßnahme
    const projectCO2Massnahme = calculateProjectAverage(comparisonGrouped, 'CO2 SOLL');
    if (projectCO2Massnahme) {
      projectMaterials.push(projectCO2Massnahme);
    }
  }

  // 4. Add IST project average (if available) - Moved to last position as requested
  if (mainIstGrouped) {
    const projectIst = calculateProjectAverage(mainIstGrouped, 'CO2 IST');
    if (projectIst) {
      projectMaterials.push(projectIst);
    }
  }

  return {
    ...mainData,
    materials: projectMaterials
  };
};

// Wrapper function to choose between layer view and project view
const combineProjectsForComparison = (mainData, comparisonData, selectedProjectName, showProjectView) => {
  if (showProjectView) {
    return combineProjectsForProjectComparison(mainData, comparisonData, selectedProjectName);
  } else {
    return combineProjectsForLayerComparison(mainData, comparisonData, selectedProjectName);
  }
};



export const Vergleich = () => {
  const { isExpanded } = useSidebar();
  const [selectedProject, setSelectedProject] = useState({
    name: null, // Will be set to most recent project
  });
  const [keyMetrics, setKeyMetrics] = useState(analyticsData.keyMetrics); // Start with mock data
  const [co2Data, setCo2Data] = useState(null); // Start with null to prevent legacy component rendering
  const [comparisonCo2Data, setComparisonCo2Data] = useState(null); // Data for comparison project
  const [timeframe, setTimeframe] = useState(null); // Project timeframe data
  const [layerAreas, setLayerAreas] = useState(null); // Construction layer areas data
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true); // Start loading immediately
  const [isLoadingCO2, setIsLoadingCO2] = useState(true); // Start loading immediately
  const [isLoadingComparison, setIsLoadingComparison] = useState(false); // Loading comparison data
  const [showProjectView, setShowProjectView] = useState(true); // Toggle between layer view and project view

  const [error, setError] = useState('');


  // Default to the last Vergleich project (kept in sync with VERGLEICH_PROJECTS).
  useEffect(() => {
    setSelectedProject(VERGLEICH_PROJECTS[VERGLEICH_PROJECTS.length - 1]);
  }, []);

  // Fetch analytics data when project changes
  useEffect(() => {
    if (selectedProject.fullName) {
      fetchAnalyticsData(selectedProject.fullName);
    }
  }, [selectedProject.fullName]);

  // Function to find the comparison project name
  const findComparisonProject = (projectName) => {
    if (!projectName) return null;

    if (projectName.includes('konventionell')) {
      return projectName.replace('konventionell', 'CO2 Maßnahme');
    } else if (projectName.includes('CO2 Maßnahme')) {
      return projectName.replace('CO2 Maßnahme', 'konventionell');
    }

    return null;
  };

  const fetchAnalyticsData = async (projectName) => {
    setIsLoadingMetrics(true);
    setIsLoadingCO2(true);
    setIsLoadingComparison(true);
    setError('');

    const comparisonProjectName = findComparisonProject(projectName);

    try {
      const [
        metricsResponse,
        co2Response,
        timeframeResponse,
        layerAreasResponse,
        comparisonCo2Response,
      ] = await Promise.all([
        analyticsService.getKeyMetrics(projectName),
        analyticsService.getCO2Breakdown(projectName),
        analyticsService.getProjectTimeframe(projectName),
        analyticsService.getLayerAreas(projectName),
        comparisonProjectName
          ? analyticsService.getCO2Breakdown(comparisonProjectName).catch((comparisonErr) => {
              console.warn(`Failed to load comparison project ${comparisonProjectName}:`, comparisonErr);
              return null;
            })
          : Promise.resolve(null),
      ]);

      if (metricsResponse.success) {
        setKeyMetrics(metricsResponse.data);
      }

      if (co2Response.success) {
        setCo2Data(co2Response.data);
      }

      if (timeframeResponse.success) {
        setTimeframe(timeframeResponse.data);
      }

      if (layerAreasResponse.success) {
        setLayerAreas(layerAreasResponse.data);
      } else {
        setLayerAreas(null);
      }

      if (comparisonCo2Response && comparisonCo2Response.success) {
        setComparisonCo2Data(comparisonCo2Response.data);
        console.log(`Loaded comparison data for: ${comparisonProjectName}`);
      } else {
        setComparisonCo2Data(null);
        if (comparisonProjectName && comparisonCo2Response) {
          console.log(`No comparison data found for: ${comparisonProjectName}`);
        }
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load analytics data');
    } finally {
      setIsLoadingMetrics(false);
      setIsLoadingCO2(false);
      setIsLoadingComparison(false);
    }
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
  };

  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';

  return (
    <div className={`${leftMargin} h-screen bg-gray-50 flex flex-col transition-all duration-300`}>
      <div className="p-8 flex-shrink-0">
        <AnalyticsHeader
          selectedProject={selectedProject}
          onProjectSelect={handleProjectSelect}
          timeframe={timeframe}
        />

        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="mt-8">
          {isLoadingMetrics ? (
            <div className="grid grid-cols-3 gap-4">
              {[...Array(3)].map((_, index) => (
                <Card key={index}>
                  <div className="p-6 flex justify-center items-center" style={{ height: '120px' }}>
                    <Loader2 className="h-6 w-6 animate-spin text-green-500" />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <KeyMetrics
              metrics={keyMetrics}
              co2Data={co2Data}
              comparisonCo2Data={comparisonCo2Data}
              selectedProject={selectedProject}
              layerAreas={layerAreas}
            />
          )}
        </div>
      </div>

      <div className="flex-1 px-8 pb-8 min-h-0">
        {isLoadingCO2 ? (
          <Card className="h-full">
            <div className="p-6 flex justify-center items-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-green-500" />
            </div>
          </Card>
        ) : (
          <div className="h-full">
            <CO2Visualization
              data={combineProjectsForComparison(co2Data, comparisonCo2Data, selectedProject.fullName, showProjectView)}
              fullHeight={true}
              title=""
              showCombinedData={false}
              showToggleButton={true}
              onToggleView={() => setShowProjectView(!showProjectView)}
              isToggleActive={showProjectView}
              toggleLabel={showProjectView ? 'Schicht-Ansicht' : 'Projekt-Ansicht'}
              useIconToggle={true}
              use1990Data={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Vergleich;